// src/admin.ts
import { z as z3 } from "zod";

// src/health.ts
import { z } from "zod";
var OperatorAlertSeveritySchema = z.enum(["CRITICAL", "HIGH", "WARN", "INFO"]);
var OperatorAlertKindSchema = z.enum([
  "tbank.session_expired",
  "tbank.otp_required",
  "tbank.poll_circuit_open",
  "tbank.unmatched_payment_spike",
  "fragment.session_invalid",
  "fragment.kyc_required",
  "fragment.delivery_failed",
  "ton.wallet_low_balance",
  "ton.rpc_degraded",
  "ton.deposit_unmatched",
  "order.stuck_delivering",
  "order.stuck_matched",
  "order.high_value_pending",
  "ledger.balance_drift_detected",
  "ledger.gram_drift_detected",
  "games.crash_high_payout",
  "games.case_jackpot_won",
  "games.house_edge_anomaly",
  "support.ticket_created",
  "support.ticket_sla_breach",
  "ban.appeal_submitted",
  "security.step_up_failed_spike",
  "security.permission_denied_spike",
  "security.large_admin_adjust",
  "broadcast.campaign_failed",
  "pricing.daily_fx_report",
  "pricing.fragment_gram_changed"
]);
var BalanceDriftDto = z.object({
  userId: z.string(),
  telegramId: z.number(),
  username: z.string().nullable(),
  displayName: z.string().nullable().optional(),
  currency: z.enum(["RUB", "GRAM"]).default("RUB"),
  storedBalance: z.number().int(),
  ledgerSum: z.number().int(),
  driftAmount: z.number().int()
});
var StuckOrderHealthDto = z.object({
  id: z.string(),
  userId: z.string(),
  telegramId: z.number().nullable(),
  username: z.string().nullable(),
  status: z.string(),
  product: z.string(),
  qty: z.number().int(),
  amountKopecks: z.number().int(),
  stuckMinutes: z.number().int(),
  updatedAt: z.string()
});
var OperatorAlertCountDto = z.object({
  kind: OperatorAlertKindSchema,
  severity: OperatorAlertSeveritySchema,
  count24h: z.number().int(),
  lastSeenAt: z.string().nullable()
});
var OperatorAlertEventDto = z.object({
  id: z.string(),
  kind: OperatorAlertKindSchema,
  severity: OperatorAlertSeveritySchema,
  title: z.string(),
  message: z.string(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  deepLinkPath: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  acknowledgedAt: z.string().nullable(),
  acknowledgedByTelegramId: z.number().nullable(),
  createdAt: z.string()
});
var ReconcileSnapshotDto = z.object({
  generatedAt: z.string(),
  healthy: z.boolean(),
  balanceDrifts: z.array(BalanceDriftDto),
  gramDrifts: z.array(BalanceDriftDto).default([]),
  stuckOrders: z.array(StuckOrderHealthDto),
  unmatchedPaymentsCount24h: z.number().int(),
  unmatchedPaymentsTotalKopecks24h: z.number().int(),
  openSupportTicketsCount: z.number().int(),
  pendingBanAppealsCount: z.number().int(),
  alertCounts24h: z.array(OperatorAlertCountDto),
  recentAlerts: z.array(OperatorAlertEventDto)
});

// src/support-tickets.ts
import { z as z2 } from "zod";
var SupportTicketCategorySchema = z2.enum([
  "payment",
  "delivery",
  "games",
  "fish",
  "account",
  "other"
]);
var SupportTicketStateSchema = z2.enum([
  "OPEN",
  "IN_PROGRESS",
  "WAITING_USER",
  "RESOLVED",
  "CLOSED"
]);
var SupportTicketEventDto = z2.object({
  id: z2.string(),
  ticketId: z2.string(),
  actorType: z2.enum(["USER", "OPERATOR", "SYSTEM"]),
  actorTelegramId: z2.number().nullable(),
  actorUsername: z2.string().nullable(),
  eventType: z2.enum(["MESSAGE", "STATE_CHANGE", "INTERNAL_NOTE"]),
  message: z2.string().nullable(),
  metadata: z2.record(z2.string(), z2.unknown()).nullable(),
  createdAt: z2.string()
});
var SupportTicketFileDto = z2.object({
  id: z2.string(),
  ticketId: z2.string(),
  telegramFileId: z2.string(),
  fileType: z2.enum(["photo", "document"]),
  fileName: z2.string().nullable(),
  mimeType: z2.string().nullable(),
  fileSizeBytes: z2.number().int().nullable(),
  uploadedByTelegramId: z2.number().nullable(),
  createdAt: z2.string()
});
var SupportTicketDto = z2.object({
  id: z2.string(),
  ticketNumber: z2.number().int(),
  userId: z2.string().nullable(),
  telegramId: z2.number(),
  username: z2.string().nullable(),
  firstName: z2.string().nullable(),
  category: SupportTicketCategorySchema,
  subject: z2.string(),
  state: SupportTicketStateSchema,
  assignedToTelegramId: z2.number().nullable(),
  assignedToUsername: z2.string().nullable(),
  relatedOrderId: z2.string().nullable(),
  messagesCount: z2.number().int(),
  filesCount: z2.number().int(),
  lastMessagePreview: z2.string().nullable(),
  lastMessageAt: z2.string(),
  createdAt: z2.string(),
  updatedAt: z2.string()
});
var MediaAttachmentScopeSchema = z2.enum([
  "support_ticket",
  "ban_appeal",
  "user_report"
]);
var MediaAttachmentDto = z2.object({
  id: z2.string(),
  fileName: z2.string().nullable(),
  mimeType: z2.string(),
  sizeBytes: z2.number().int(),
  url: z2.string().nullable(),
  expiresAt: z2.string(),
  expired: z2.boolean().default(false),
  createdAt: z2.string()
});
var UploadMediaResponse = z2.object({
  attachments: z2.array(MediaAttachmentDto)
});
var SupportTicketDetailDto = SupportTicketDto.extend({
  events: z2.array(SupportTicketEventDto),
  files: z2.array(SupportTicketFileDto),
  attachments: z2.array(MediaAttachmentDto).default([])
});
var AdminReplySupportTicketRequestDto = z2.object({
  message: z2.string().min(1).max(4e3).optional(),
  internalNote: z2.string().min(1).max(2e3).optional(),
  nextState: SupportTicketStateSchema.optional(),
  assignToSelf: z2.boolean().optional(),
  reason: z2.string().min(3).max(500).optional()
});

// src/admin.ts
var PlatformRoleSchema = z3.enum([
  "superadministrator",
  "administrator",
  "support"
]);
var PlatformPermissionSchema = z3.enum([
  "admin.read",
  "order.manage",
  "order.force_match",
  "payment.match",
  "admin.user.manage",
  "admin.user.suspend",
  "admin.tamagotchi.manage",
  "admin.role.manage",
  "support.manage",
  "broadcast.manage",
  "banners.manage",
  "balance.admin_adjust",
  "config.economy.update",
  "config.referrals.update",
  "treasury.read",
  "games.manage",
  "quests.manage",
  "fragment.session.manage",
  "tbank.session.manage",
  "audit.read"
]);
var StepUpActionSchema = z3.enum([
  "balance.admin_adjust",
  "order.force_match",
  "fragment.session.update",
  "tbank.session.manage",
  "user.role.set",
  "config.economy.update",
  "config.games.update",
  "broadcast.send"
]);
var AdminAuthSessionDto = z3.object({
  sessionId: z3.string(),
  userId: z3.string().nullable(),
  telegramId: z3.number(),
  username: z3.string().nullable(),
  firstName: z3.string().nullable(),
  roles: z3.array(PlatformRoleSchema),
  permissions: z3.array(PlatformPermissionSchema),
  expiresAt: z3.string()
});
var AdminStepUpGrantDto = z3.object({
  grantToken: z3.string(),
  action: StepUpActionSchema,
  expiresAt: z3.string()
});
var AdminActionBatchItemKindSchema = z3.enum([
  "order.force_match",
  "order.retry_delivery",
  "order.cancel_refund",
  "payment.manual_match",
  "payment.ignore",
  "balance.adjust",
  "balance.gram_adjust",
  "user.xp_adjust",
  "user.fish_action",
  "user.companion_grant",
  "user.companion_revoke",
  "user.ban",
  "user.unban",
  "user.role_set",
  "user.verify_toggle",
  "user.ref_code_set",
  "user.reset_profile",
  "user.send_dm",
  "ban_appeal.resolve",
  "support_ticket.reply",
  "case.config_update",
  "crash.config_update",
  "quest.upsert",
  "banner.upsert",
  "settings.update",
  "referral.config_update"
]);
var AdminExecuteActionRequest = z3.object({
  kind: AdminActionBatchItemKindSchema,
  targetId: z3.string().min(1),
  payload: z3.record(z3.string(), z3.unknown()).default({}),
  reason: z3.string().min(3).max(500),
  stepUpGrantToken: z3.string().optional()
});
var AdminActionBatchItemDto = z3.object({
  id: z3.string(),
  batchId: z3.string(),
  itemIndex: z3.number().int(),
  kind: AdminActionBatchItemKindSchema,
  targetId: z3.string(),
  payload: z3.record(z3.string(), z3.unknown()),
  status: z3.enum(["PENDING", "EXECUTED", "FAILED"]),
  result: z3.record(z3.string(), z3.unknown()).nullable(),
  errorMessage: z3.string().nullable(),
  executedAt: z3.string().nullable()
});
var AdminActionBatchDto = z3.object({
  id: z3.string(),
  actorTelegramId: z3.number(),
  actorUsername: z3.string().nullable(),
  reason: z3.string(),
  status: z3.enum(["DRAFT", "AWAITING_CONFIRM", "EXECUTED", "PARTIAL_FAILED", "CANCELLED", "EXPIRED"]),
  items: z3.array(AdminActionBatchItemDto),
  createdAt: z3.string(),
  executedAt: z3.string().nullable(),
  expiresAt: z3.string()
});
var AdminDailyStatRowDto = z3.object({
  day: z3.string(),
  ordersCount: z3.number().int(),
  deliveredOrdersCount: z3.number().int(),
  starsSold: z3.number().int(),
  premiumMonthsSold: z3.number().int(),
  grossRevenueKopecks: z3.number().int(),
  netProfitKopecks: z3.number().int(),
  depositsKopecks: z3.number().int(),
  gramDepositsMilli: z3.number().int().default(0),
  newUsersCount: z3.number().int(),
  activeUsersCount: z3.number().int(),
  crashBetsKopecks: z3.number().int(),
  crashPayoutsKopecks: z3.number().int(),
  casesOpenedCount: z3.number().int(),
  casesRevenueKopecks: z3.number().int(),
  casesPayoutsKopecks: z3.number().int(),
  referralRewardsKopecks: z3.number().int()
});
var AdminKpiMetricDto = z3.object({
  key: z3.string(),
  label: z3.string(),
  value: z3.number(),
  previousValue: z3.number(),
  deltaPercent: z3.number(),
  unit: z3.enum(["RUB", "GRAM", "STARS", "PERCENT", "COUNT"])
});
var AdminAuditLogDto = z3.object({
  id: z3.string(),
  action: z3.string(),
  actorId: z3.string().nullable(),
  actorType: z3.enum(["ADMIN", "USER", "SYSTEM", "WORKER"]),
  actorTelegramId: z3.number().nullable(),
  actorUsername: z3.string().nullable(),
  entityType: z3.string(),
  entityId: z3.string().nullable(),
  correlationId: z3.string().nullable(),
  privileged: z3.boolean(),
  reason: z3.string().nullable(),
  metadata: z3.record(z3.string(), z3.unknown()).nullable(),
  createdAt: z3.string()
});
var AdminCategoryBreakdownItemDto = z3.object({
  key: z3.string(),
  label: z3.string(),
  ordersCount: z3.number().int(),
  revenueKopecks: z3.number().int()
});
var AdminCategoryBreakdownResponseDto = z3.object({
  from: z3.string().nullable(),
  to: z3.string().nullable(),
  items: z3.array(AdminCategoryBreakdownItemDto)
});
var AdminDashboardDto = z3.object({
  from: z3.string(),
  to: z3.string(),
  kpis: z3.array(AdminKpiMetricDto),
  dailySeries: z3.array(AdminDailyStatRowDto),
  queues: z3.object({
    awaitingOrdersCount: z3.number().int(),
    stuckOrdersCount: z3.number().int(),
    unmatchedPaymentsCount: z3.number().int(),
    openSupportTicketsCount: z3.number().int(),
    pendingBanAppealsCount: z3.number().int(),
    openUserReportsCount: z3.number().int().optional()
  }),
  categoryBreakdown: z3.array(AdminCategoryBreakdownItemDto).optional(),
  systemMetrics: z3.object({
    cpuLoadPercent: z3.number(),
    memoryUsedPercent: z3.number(),
    uptimeSeconds: z3.number().int()
  }).optional(),
  recentAuditLogs: z3.array(AdminAuditLogDto),
  reconcile: ReconcileSnapshotDto
});
var AdminSearchResponseDto = z3.object({
  query: z3.string(),
  users: z3.array(
    z3.object({
      id: z3.string(),
      telegramId: z3.number(),
      username: z3.string().nullable(),
      firstName: z3.string().nullable(),
      customName: z3.string().nullable().optional(),
      photoUrl: z3.string().nullable().optional(),
      customPhotoUrl: z3.string().nullable().optional(),
      isVerified: z3.boolean().optional(),
      balanceKopecks: z3.number().int(),
      balanceMilliGram: z3.number().int().default(0),
      xp: z3.number().int(),
      isBanned: z3.boolean()
    })
  ),
  orders: z3.array(
    z3.object({
      id: z3.string(),
      userId: z3.string(),
      telegramId: z3.number().nullable(),
      username: z3.string().nullable(),
      product: z3.string(),
      qty: z3.number().int(),
      status: z3.string(),
      expectedAmountKopecks: z3.number().int(),
      createdAt: z3.string()
    })
  ),
  payments: z3.array(
    z3.object({
      id: z3.string(),
      externalId: z3.string(),
      amountKopecks: z3.number().int(),
      senderName: z3.string().nullable(),
      matchedOrderId: z3.string().nullable(),
      receivedAt: z3.string()
    })
  ),
  tickets: z3.array(
    z3.object({
      id: z3.string(),
      ticketNumber: z3.number().int(),
      telegramId: z3.number(),
      username: z3.string().nullable(),
      category: z3.string(),
      subject: z3.string(),
      state: z3.string()
    })
  )
});
var AdminFxDto = z3.object({
  gramUsd: z3.number().default(1.25),
  tonUsd: z3.number(),
  tonRub: z3.number(),
  usdRub: z3.number(),
  gramRub: z3.number().default(1),
  updatedAt: z3.string()
});
var AdminBotStarsSummaryDto = z3.object({
  available: z3.boolean().default(false),
  totalStars: z3.number().int().default(0),
  availableToWithdrawStars: z3.number().int().default(0),
  heldStars: z3.number().int().default(0),
  nextUnlockAt: z3.string().nullable().default(null),
  recentHeldTransactions: z3.array(
    z3.object({
      id: z3.string(),
      stars: z3.number().int(),
      createdAt: z3.string(),
      unlocksAt: z3.string()
    })
  ).default([]),
  error: z3.string().nullable().optional()
});
var AdminTreasuryDto = z3.object({
  tonWallet: z3.object({
    address: z3.string().nullable(),
    balanceNano: z3.string(),
    balanceTon: z3.number(),
    reservedTon: z3.number(),
    availableTon: z3.number(),
    healthy: z3.boolean(),
    updatedAt: z3.string()
  }),
  botStars: AdminBotStarsSummaryDto.optional(),
  fragment: z3.object({
    connected: z3.boolean(),
    mode: z3.string(),
    kycStatus: z3.string(),
    accountLabel: z3.string().nullable(),
    lastCheckedAt: z3.string().nullable()
  }),
  tbank: z3.object({
    state: z3.string(),
    maskedPhone: z3.string().nullable(),
    lastPollAt: z3.string().nullable(),
    circuitOpen: z3.boolean()
  }),
  unitEconomics: z3.object({
    starSellPriceRub: z3.number(),
    fragmentStarCostTon: z3.number(),
    fragmentStarCostRub: z3.number(),
    grossMarginPerStarRub: z3.number(),
    grossMarginPercent: z3.number()
  }),
  liabilities: z3.object({
    totalUserBalanceKopecks: z3.number().int(),
    totalUserBalanceMilliGram: z3.number().int().default(0),
    usersWithPositiveBalanceCount: z3.number().int(),
    ledgerDriftUsersCount: z3.number().int()
  }),
  coverage: z3.object({
    rubLiabilitiesKopecks: z3.number().int(),
    gramLiabilitiesMilli: z3.number().int(),
    gramWalletMilli: z3.number().int(),
    gramSurplusOrDeficitMilli: z3.number().int(),
    gramCovered: z3.boolean(),
    totalLiabilitiesRubEquivKopecks: z3.number().int(),
    totalLiquidAssetsRubEquivKopecks: z3.number().int()
  }).optional(),
  drifts: z3.array(BalanceDriftDto).default([]),
  pnl: z3.object({
    allTimeGrossRevenueKopecks: z3.number().int(),
    allTimeNetProfitKopecks: z3.number().int(),
    last7dGrossRevenueKopecks: z3.number().int(),
    last7dNetProfitKopecks: z3.number().int(),
    crashHouseProfitKopecks: z3.number().int(),
    casesHouseProfitKopecks: z3.number().int(),
    referralExpenseKopecks: z3.number().int()
  }),
  fx: AdminFxDto
});
var AdminTransactionRowDto = z3.object({
  id: z3.string(),
  userId: z3.string(),
  telegramId: z3.number().nullable(),
  username: z3.string().nullable(),
  type: z3.string(),
  currency: z3.enum(["RUB", "GRAM"]).default("RUB"),
  amountKopecks: z3.number().int(),
  balanceBeforeKopecks: z3.number().int(),
  balanceAfterKopecks: z3.number().int(),
  referenceId: z3.string().nullable(),
  correlationId: z3.string().nullable(),
  description: z3.string(),
  createdAt: z3.string()
});
var AdminGamesOverviewDto = z3.object({
  crash: z3.object({
    houseEdgePercent: z3.number(),
    minBetRub: z3.number(),
    maxBetRub: z3.number(),
    totalRoundsCount: z3.number().int(),
    totalBetsCount: z3.number().int(),
    totalWageredKopecks: z3.number().int(),
    totalPayoutsKopecks: z3.number().int(),
    houseProfitKopecks: z3.number().int(),
    realizedRtpPercent: z3.number(),
    recentRounds: z3.array(
      z3.object({
        id: z3.string(),
        status: z3.string(),
        crashPoint: z3.number().nullable(),
        betsCount: z3.number().int(),
        wageredKopecks: z3.number().int(),
        payoutsKopecks: z3.number().int(),
        startedAt: z3.string().nullable(),
        crashedAt: z3.string().nullable()
      })
    )
  }),
  cases: z3.object({
    totalOpeningsCount: z3.number().int(),
    totalRevenueKopecks: z3.number().int(),
    totalPayoutsKopecks: z3.number().int(),
    houseProfitKopecks: z3.number().int(),
    realizedRtpPercent: z3.number(),
    definitions: z3.array(
      z3.object({
        id: z3.string(),
        name: z3.string(),
        priceRub: z3.number(),
        priceGram: z3.number().optional(),
        variant: z3.string(),
        totalWeight: z3.number(),
        expectedRubKopecks: z3.number().int(),
        rtpPercent: z3.number(),
        loot: z3.array(
          z3.object({
            type: z3.enum(["rub", "xp", "stars_discount", "brainrot"]),
            value: z3.union([z3.number(), z3.string()]),
            weight: z3.number(),
            label: z3.string().optional()
          })
        )
      })
    ),
    recentOpenings: z3.array(
      z3.object({
        id: z3.string(),
        userId: z3.string(),
        telegramId: z3.number().nullable(),
        username: z3.string().nullable(),
        caseId: z3.string(),
        priceKopecks: z3.number().int(),
        rewardType: z3.string(),
        rewardValue: z3.string(),
        createdAt: z3.string()
      })
    )
  })
});
var AdminReferralOverviewDto = z3.object({
  config: z3.object({
    inviterBonusXp: z3.number().int(),
    inviteeDiscountPercent: z3.number(),
    generationsCount: z3.number().int().min(1).max(10).default(2),
    generationPercents: z3.array(z3.number()).default([5, 1.5]),
    commissionPercentL1: z3.number(),
    commissionPercentL2: z3.number()
  }),
  kpis: z3.object({
    totalPairsCount: z3.number().int(),
    qualifiedPairsCount: z3.number().int(),
    totalBonusXpAwarded: z3.number().int(),
    totalCommissionKopecks: z3.number().int()
  }),
  topInviters: z3.array(
    z3.object({
      userId: z3.string(),
      telegramId: z3.number(),
      username: z3.string().nullable(),
      firstName: z3.string().nullable(),
      customName: z3.string().nullable().optional(),
      photoUrl: z3.string().nullable().optional(),
      refCode: z3.string().optional(),
      invitedCount: z3.number().int(),
      qualifiedCount: z3.number().int(),
      earnedKopecks: z3.number().int(),
      earnedXp: z3.number().int()
    })
  ),
  recentPairs: z3.array(
    z3.object({
      id: z3.string(),
      inviterUserId: z3.string(),
      inviterTelegramId: z3.number().nullable(),
      inviterUsername: z3.string().nullable(),
      inviterDisplayName: z3.string().nullable().optional(),
      inviterPhotoUrl: z3.string().nullable().optional(),
      inviteeUserId: z3.string(),
      inviteeTelegramId: z3.number().nullable(),
      inviteeUsername: z3.string().nullable(),
      inviteeDisplayName: z3.string().nullable().optional(),
      inviteePhotoUrl: z3.string().nullable().optional(),
      qualified: z3.boolean(),
      rewardedKopecks: z3.number().int(),
      createdAt: z3.string()
    })
  ),
  recentAccruals: z3.array(
    z3.object({
      id: z3.string(),
      inviterUserId: z3.string(),
      inviterTelegramId: z3.number().nullable(),
      inviterUsername: z3.string().nullable().optional(),
      inviterDisplayName: z3.string().nullable().optional(),
      inviteeUserId: z3.string(),
      inviteeTelegramId: z3.number().nullable(),
      inviteeUsername: z3.string().nullable().optional(),
      inviteeDisplayName: z3.string().nullable().optional(),
      orderId: z3.string().nullable(),
      level: z3.number().int(),
      amountKopecks: z3.number().int(),
      xpAwarded: z3.number().int(),
      createdAt: z3.string()
    })
  )
});
var AdminReferralTreeNodeDto = z3.object({
  userId: z3.string(),
  telegramId: z3.number(),
  username: z3.string().nullable(),
  firstName: z3.string().nullable(),
  customName: z3.string().nullable().optional(),
  photoUrl: z3.string().nullable().optional(),
  isVerified: z3.boolean().optional(),
  level: z3.number().int(),
  qualified: z3.boolean(),
  earnedForRootKopecks: z3.number().int(),
  createdAt: z3.string()
});
var AdminReferralTreeDto = z3.object({
  rootUser: z3.object({
    userId: z3.string(),
    telegramId: z3.number(),
    username: z3.string().nullable(),
    firstName: z3.string().nullable(),
    customName: z3.string().nullable().optional(),
    photoUrl: z3.string().nullable().optional(),
    referralCode: z3.string()
  }),
  upline: AdminReferralTreeNodeDto.nullable(),
  downlineL1: z3.array(AdminReferralTreeNodeDto),
  downlineL2: z3.array(AdminReferralTreeNodeDto),
  generations: z3.array(
    z3.object({
      level: z3.number().int(),
      percent: z3.number(),
      nodes: z3.array(AdminReferralTreeNodeDto)
    })
  ).optional()
});
var AdminUserDeviceDto = z3.object({
  id: z3.string(),
  userId: z3.string(),
  deviceFingerprint: z3.string(),
  platform: z3.string().nullable(),
  telegramClient: z3.string().nullable().optional(),
  telegramVersion: z3.string().nullable().optional(),
  osName: z3.string().nullable().optional(),
  deviceModel: z3.string().nullable().optional(),
  browserName: z3.string().nullable().optional(),
  languageCode: z3.string().nullable().optional(),
  screenResolution: z3.string().nullable().optional(),
  timezone: z3.string().nullable().optional(),
  userAgent: z3.string().nullable(),
  ipAddress: z3.string().nullable(),
  countryCode: z3.string().nullable().optional(),
  countryName: z3.string().nullable().optional(),
  regionName: z3.string().nullable().optional(),
  cityName: z3.string().nullable().optional(),
  ispName: z3.string().nullable().optional(),
  firstSeenAt: z3.string(),
  lastSeenAt: z3.string()
});
var AdminUserLoginEventDto = z3.object({
  id: z3.string(),
  userId: z3.string(),
  source: z3.string(),
  ipAddress: z3.string().nullable(),
  telegramClient: z3.string().nullable(),
  telegramVersion: z3.string().nullable(),
  osName: z3.string().nullable(),
  deviceModel: z3.string().nullable(),
  browserName: z3.string().nullable(),
  userAgent: z3.string().nullable(),
  languageCode: z3.string().nullable(),
  timezone: z3.string().nullable(),
  screenResolution: z3.string().nullable(),
  isTelegramPremium: z3.boolean(),
  countryCode: z3.string().nullable(),
  countryName: z3.string().nullable(),
  regionName: z3.string().nullable(),
  cityName: z3.string().nullable(),
  ispName: z3.string().nullable(),
  createdAt: z3.string()
});
var AdminUserReferralStatsDto = z3.object({
  code: z3.string(),
  link: z3.string(),
  invitedCount: z3.number().int(),
  qualifiedCount: z3.number().int(),
  conversionPercent: z3.number(),
  paidOrdersCount: z3.number().int(),
  turnoverKopecks: z3.number().int(),
  totalEarnedKopecks: z3.number().int(),
  totalEarnedXp: z3.number().int(),
  byGeneration: z3.array(
    z3.object({
      level: z3.number().int(),
      percent: z3.number(),
      invitedCount: z3.number().int(),
      accrualsCount: z3.number().int(),
      earnedKopecks: z3.number().int()
    })
  )
});
var AdminUserSessionDto = z3.object({
  id: z3.string(),
  userId: z3.string().nullable(),
  telegramId: z3.number(),
  username: z3.string().nullable(),
  kind: z3.enum(["MINIAPP", "ADMIN"]),
  ipAddress: z3.string().nullable(),
  userAgent: z3.string().nullable(),
  createdAt: z3.string(),
  lastUsedAt: z3.string(),
  expiresAt: z3.string(),
  revokedAt: z3.string().nullable()
});
var AdminUsersSortBySchema = z3.enum([
  "createdAt",
  "balanceRub",
  "balanceGram",
  "deliveredOrders"
]);
var AdminSortDirectionSchema = z3.enum(["asc", "desc"]);
var AdminUserFinancialSummaryDto = z3.object({
  deliveredOrdersCount: z3.number().int(),
  grossOrdersKopecks: z3.number().int(),
  externalPaidOrdersKopecks: z3.number().int(),
  depositsRubKopecks: z3.number().int(),
  depositsGramMilli: z3.number().int(),
  depositsGramRubEquivKopecks: z3.number().int(),
  totalBroughtKopecks: z3.number().int(),
  ordersGrossMarginKopecks: z3.number().int(),
  referralCommissionsPaidKopecks: z3.number().int(),
  gamesNetProfitKopecks: z3.number().int(),
  netPlatformProfitKopecks: z3.number().int()
});
var AdminLinkedAccountReasonDto = z3.object({
  kind: z3.enum(["device", "ip", "payment", "gift", "referral"]),
  label: z3.string(),
  detail: z3.string()
});
var AdminLinkedAccountDto = z3.object({
  userId: z3.string(),
  telegramId: z3.number(),
  username: z3.string().nullable(),
  firstName: z3.string().nullable(),
  customName: z3.string().nullable().optional(),
  photoUrl: z3.string().nullable().optional(),
  customPhotoUrl: z3.string().nullable().optional(),
  isVerified: z3.boolean().default(false),
  isBanned: z3.boolean().default(false),
  confidence: z3.enum(["HIGH", "MEDIUM"]),
  score: z3.number().int(),
  reasons: z3.array(AdminLinkedAccountReasonDto)
});
var AdminSpendCommissionTierDto = z3.object({
  minSpentRub: z3.number().gt(0),
  starsFeePercent: z3.number().min(0).max(900),
  premiumFeePercent: z3.number().min(0).max(900)
});
var AdminUpdateSpendTiersSchema = z3.object({
  tiers: z3.array(AdminSpendCommissionTierDto).max(20)
});
var AdminUserCommissionDto = z3.object({
  source: z3.enum(["general", "spend_tier", "personal"]),
  starsSource: z3.enum(["general", "spend_tier", "personal"]),
  premiumSource: z3.enum(["general", "spend_tier", "personal"]),
  starsFeePercent: z3.number(),
  premiumFeePercent: z3.number(),
  starsMarkup: z3.number(),
  premiumMarkup: z3.number(),
  customStarsFeePercent: z3.number().nullable(),
  customPremiumFeePercent: z3.number().nullable(),
  spentRub: z3.number(),
  currentTierMinRub: z3.number().nullable(),
  nextTier: z3.object({
    minSpentRub: z3.number(),
    remainingRub: z3.number(),
    starsFeePercent: z3.number(),
    premiumFeePercent: z3.number()
  }).nullable()
});
var AdminUpdateUserCommissionSchema = z3.object({
  customStarsFeePercent: z3.number().min(0).max(900).nullable().optional(),
  customPremiumFeePercent: z3.number().min(0).max(900).nullable().optional(),
  reason: z3.string().trim().max(500).optional()
});
var AdminReferralCodeDto = z3.object({
  id: z3.string(),
  code: z3.string(),
  ownerUserId: z3.string().nullable(),
  ownerTelegramId: z3.number().nullable(),
  ownerUsername: z3.string().nullable(),
  ownerDisplayName: z3.string().nullable(),
  title: z3.string().nullable(),
  firstOrderDiscountPercent: z3.number(),
  firstDepositBonusPercent: z3.number(),
  maxUses: z3.number().int().nullable(),
  usesCount: z3.number().int(),
  active: z3.boolean(),
  isUserDefaultCode: z3.boolean().default(false),
  expiresAt: z3.string().nullable(),
  createdAt: z3.string(),
  updatedAt: z3.string()
});
var AdminUpdateUserRefCodePerksSchema = z3.object({
  refCode: z3.string().trim().min(3).max(32).optional(),
  firstOrderDiscountPercent: z3.number().min(0).max(50).default(0),
  firstDepositBonusPercent: z3.number().min(0).max(100).default(0),
  reason: z3.string().trim().max(500).optional()
});
var AdminFragmentSessionDetailDto = z3.object({
  status: z3.enum(["loaded", "empty", "error"]),
  ready: z3.boolean(),
  verified: z3.boolean(),
  verificationDetail: z3.string(),
  hasRecord: z3.boolean(),
  source: z3.enum(["db", "file", "none"]).default("none"),
  ageDays: z3.number().nullable(),
  platformTelegramUserId: z3.number().nullable(),
  updatedByTelegramId: z3.number().nullable(),
  lastRefreshedAt: z3.string().nullable(),
  lastCheckedAt: z3.string().nullable(),
  lastSessionCheckOkAt: z3.string().nullable(),
  cookieKeysPresent: z3.array(z3.string()).default([])
});
var AdminOperatorEntryDto = z3.object({
  telegramId: z3.number(),
  userId: z3.string().nullable(),
  username: z3.string().nullable(),
  displayName: z3.string().nullable(),
  photoUrl: z3.string().nullable(),
  roles: z3.array(PlatformRoleSchema),
  isBootstrapSuperAdmin: z3.boolean().default(false),
  lastSeenAt: z3.string().nullable()
});
var AdminUpsertReferralCodeSchema = z3.object({
  code: z3.string().trim().min(3).max(32),
  ownerQuery: z3.string().trim().max(128).nullable().optional(),
  ownerUserId: z3.string().trim().max(128).nullable().optional(),
  title: z3.string().trim().max(160).nullable().optional(),
  firstOrderDiscountPercent: z3.number().min(0).max(50).default(0),
  firstDepositBonusPercent: z3.number().min(0).max(100).default(0),
  maxUses: z3.number().int().min(1).max(1e6).nullable().optional(),
  expiresAt: z3.string().nullable().optional(),
  active: z3.boolean().default(true)
});
var AdminUserDetailDto = z3.object({
  id: z3.string(),
  telegramId: z3.number(),
  username: z3.string().nullable(),
  firstName: z3.string().nullable(),
  customName: z3.string().nullable().optional(),
  photoUrl: z3.string().nullable(),
  customPhotoUrl: z3.string().nullable().optional(),
  hideUsername: z3.boolean().default(false),
  referralCode: z3.string(),
  referralDiscountUsed: z3.boolean().default(false),
  firstDepositBonusUsed: z3.boolean().default(false),
  appliedReferralCode: z3.string().nullable().optional(),
  personalReferralCodePerks: z3.object({
    firstOrderDiscountPercent: z3.number().default(0),
    firstDepositBonusPercent: z3.number().default(0),
    hasCustomRow: z3.boolean().default(false)
  }).optional(),
  referralStats: AdminUserReferralStatsDto.optional(),
  commission: AdminUserCommissionDto.optional(),
  deliveredOrdersCount: z3.number().int().default(0),
  financialSummary: AdminUserFinancialSummaryDto.optional(),
  linkedAccounts: z3.array(AdminLinkedAccountDto).default([]),
  balanceKopecks: z3.number().int(),
  balanceMilliGram: z3.number().int().default(0),
  xp: z3.number().int(),
  level: z3.number().int(),
  levelTitle: z3.string(),
  purchasedStars: z3.number().int(),
  premiumCount: z3.number().int(),
  friendsCount: z3.number().int(),
  streak: z3.number().int(),
  activeBrainrot: z3.string(),
  ownedCompanions: z3.array(z3.string()),
  onboarded: z3.boolean(),
  isVerified: z3.boolean(),
  roles: z3.array(PlatformRoleSchema),
  ban: z3.object({
    id: z3.string(),
    bannedAt: z3.string(),
    bannedUntil: z3.string().nullable(),
    reason: z3.string(),
    bannedByTelegramId: z3.number().nullable(),
    activeAppealId: z3.string().nullable()
  }).nullable(),
  fish: z3.object({
    hunger: z3.number().int(),
    happiness: z3.number().int(),
    health: z3.number().int(),
    status: z3.enum(["thriving", "ok", "hungry", "critical", "dead"]),
    isDead: z3.boolean(),
    xpMultiplier: z3.number(),
    lastFedAt: z3.string().nullable(),
    lastPlayedAt: z3.string().nullable()
  }),
  createdAt: z3.string(),
  updatedAt: z3.string(),
  recentOrders: z3.array(z3.record(z3.string(), z3.unknown())),
  recentTransactions: z3.array(AdminTransactionRowDto),
  recentGameBets: z3.array(z3.record(z3.string(), z3.unknown())),
  recentCaseOpenings: z3.array(z3.record(z3.string(), z3.unknown())),
  devices: z3.array(AdminUserDeviceDto),
  loginEvents: z3.array(AdminUserLoginEventDto).default([]),
  sessions: z3.array(AdminUserSessionDto),
  auditLogs: z3.array(AdminAuditLogDto)
});
var AdminUserReportDto = z3.object({
  id: z3.string(),
  reportNumber: z3.number().int(),
  reporterUserId: z3.string(),
  reporterTelegramId: z3.number().nullable(),
  reporterUsername: z3.string().nullable(),
  reporterDisplayName: z3.string().nullable(),
  targetUserId: z3.string(),
  targetTelegramId: z3.number().nullable(),
  targetUsername: z3.string().nullable(),
  targetFirstName: z3.string().nullable(),
  targetCustomName: z3.string().nullable(),
  targetPhotoUrl: z3.string().nullable(),
  targetCustomPhotoUrl: z3.string().nullable(),
  targetIsBanned: z3.boolean(),
  context: z3.string(),
  reason: z3.string(),
  comment: z3.string().nullable(),
  snapshotName: z3.string().nullable(),
  snapshotUsername: z3.string().nullable(),
  snapshotPhotoUrl: z3.string().nullable(),
  state: z3.enum(["OPEN", "RESOLVED", "DISMISSED"]),
  resolutionAction: z3.string().nullable(),
  resolutionNote: z3.string().nullable(),
  resolvedByTelegramId: z3.number().nullable(),
  resolvedAt: z3.string().nullable(),
  attachments: z3.array(MediaAttachmentDto).default([]),
  createdAt: z3.string()
});
var AdminResolveUserReportRequestDto = z3.object({
  action: z3.enum(["RESET_NAME", "RESET_AVATAR", "RESET_BOTH", "BAN_USER", "DISMISS"]),
  note: z3.string().trim().max(500).optional()
});
var AdminBanAppealEventDto = z3.object({
  id: z3.string(),
  appealId: z3.string(),
  actorType: z3.enum(["USER", "OPERATOR", "SYSTEM"]),
  actorTelegramId: z3.number().nullable(),
  actorUsername: z3.string().nullable(),
  eventType: z3.enum(["SUBMITTED", "MESSAGE", "INFO_REQUESTED", "APPROVED", "REJECTED"]),
  message: z3.string().nullable(),
  createdAt: z3.string()
});
var AdminBanAppealDto = z3.object({
  id: z3.string(),
  appealNumber: z3.number().int(),
  banRecordId: z3.string(),
  userId: z3.string(),
  telegramId: z3.number(),
  username: z3.string().nullable(),
  firstName: z3.string().nullable(),
  banReason: z3.string(),
  bannedAt: z3.string(),
  bannedUntil: z3.string().nullable(),
  appealText: z3.string(),
  state: z3.enum(["OPEN", "INFO_REQUESTED", "APPROVED", "REJECTED"]),
  resolutionNote: z3.string().nullable(),
  resolvedByTelegramId: z3.number().nullable(),
  resolvedAt: z3.string().nullable(),
  events: z3.array(AdminBanAppealEventDto),
  attachments: z3.array(MediaAttachmentDto).default([]),
  createdAt: z3.string(),
  updatedAt: z3.string()
});
var AdminBroadcastCampaignDto = z3.object({
  id: z3.string(),
  title: z3.string(),
  messageHtml: z3.string(),
  buttonLabel: z3.string().nullable(),
  buttonUrl: z3.string().nullable(),
  audienceSegment: z3.enum([
    "all",
    "buyers",
    "non_buyers",
    "dead_fish",
    "vip_lvl5_plus",
    "inactive_7d"
  ]),
  state: z3.enum(["DRAFT", "SENDING", "COMPLETED", "CANCELLED", "FAILED"]),
  totalRecipients: z3.number().int(),
  sentCount: z3.number().int(),
  failedCount: z3.number().int(),
  blockedCount: z3.number().int(),
  createdByTelegramId: z3.number().nullable(),
  createdByUsername: z3.string().nullable(),
  startedAt: z3.string().nullable(),
  completedAt: z3.string().nullable(),
  createdAt: z3.string()
});
var FragmentPremiumTierRateSchema = z3.object({
  months: z3.union([z3.literal(3), z3.literal(6), z3.literal(12)]),
  baseGram: z3.number(),
  baseUsd: z3.number(),
  discountPercent: z3.number()
});
var AdminPricingLiveComparisonDto = z3.object({
  lockedAt: z3.string(),
  lockedByTelegramId: z3.number().nullable().default(null),
  liveCheckedAt: z3.string(),
  liveFragment: z3.object({
    baseStarGram: z3.number(),
    baseStarUsd: z3.number(),
    premium: z3.object({
      "3": FragmentPremiumTierRateSchema,
      "6": FragmentPremiumTierRateSchema,
      "12": FragmentPremiumTierRateSchema
    }),
    updatedAt: z3.string(),
    source: z3.enum(["live", "cache", "BootstrapFallback"])
  }),
  liveFx: z3.object({
    gramUsd: z3.number(),
    gramRub: z3.number(),
    usdRub: z3.number(),
    updatedAt: z3.string()
  }),
  hasChanges: z3.boolean(),
  fxChanged: z3.boolean(),
  fragmentGramChanged: z3.boolean(),
  dismissed: z3.boolean().default(false),
  candidateSignature: z3.string(),
  gramRubDelta: z3.number(),
  gramRubDeltaPercent: z3.number(),
  usdRubDelta: z3.number(),
  usdRubDeltaPercent: z3.number(),
  star1: z3.object({
    lockedRub: z3.number(),
    liveRub: z3.number(),
    deltaRub: z3.number(),
    deltaPercent: z3.number(),
    lockedBaseGram: z3.number(),
    liveBaseGram: z3.number(),
    deltaBaseGram: z3.number()
  }),
  stars100: z3.object({
    lockedRub: z3.number(),
    liveRub: z3.number(),
    deltaRub: z3.number(),
    deltaPercent: z3.number()
  }),
  premium: z3.array(
    z3.object({
      months: z3.union([z3.literal(3), z3.literal(6), z3.literal(12)]),
      lockedRub: z3.number(),
      liveRub: z3.number(),
      deltaRub: z3.number(),
      deltaPercent: z3.number(),
      lockedBaseGram: z3.number(),
      liveBaseGram: z3.number(),
      deltaBaseGram: z3.number()
    })
  )
});
var AdminPricingPreviewDto = z3.object({
  fragment: z3.object({
    baseStarGram: z3.number(),
    baseStarUsd: z3.number(),
    premium: z3.object({
      "3": FragmentPremiumTierRateSchema,
      "6": FragmentPremiumTierRateSchema,
      "12": FragmentPremiumTierRateSchema
    }),
    updatedAt: z3.string(),
    source: z3.enum(["live", "cache", "BootstrapFallback"])
  }),
  fx: z3.object({
    gramUsd: z3.number(),
    gramRub: z3.number(),
    usdRub: z3.number(),
    updatedAt: z3.string()
  }),
  starsMarkupMultiplier: z3.number(),
  premiumMarkupMultiplier: z3.number(),
  paymentMethod: z3.enum(["ton", "usdt_ton"]),
  spendTiers: z3.array(AdminSpendCommissionTierDto).default([]),
  liveComparison: AdminPricingLiveComparisonDto.optional()
});

// src/banners.ts
import { z as z4 } from "zod";
var BannerPlacementSchema = z4.enum([
  "stars_top",
  "aquarium_top",
  "cases_top",
  "games_top",
  "profile_top"
]);
var BannerTemplateSchema = z4.enum(["hero", "compact", "split", "minimal"]);
var BannerPresetSchema = z4.enum([
  "ocean_cyan",
  "sunset_coral",
  "gold_treasure",
  "abyss_violet",
  "emerald_reef"
]);
var PublicBannerDto = z4.object({
  id: z4.string(),
  placement: BannerPlacementSchema,
  template: BannerTemplateSchema,
  preset: BannerPresetSchema,
  eyebrow: z4.string().nullable(),
  title: z4.string(),
  subtitle: z4.string().nullable(),
  badge: z4.string().nullable(),
  ctaLabel: z4.string().nullable(),
  ctaUrl: z4.string().nullable(),
  imageDataUrl: z4.string().nullable(),
  dismissible: z4.boolean(),
  priority: z4.number().int()
});
var PublicBannersResponseDto = z4.object({
  banners: z4.array(PublicBannerDto)
});
var AdminBannerDto = PublicBannerDto.extend({
  active: z4.boolean(),
  startsAt: z4.string().nullable(),
  endsAt: z4.string().nullable(),
  dismissalsCount: z4.number().int(),
  createdByTelegramId: z4.number().nullable(),
  createdAt: z4.string(),
  updatedAt: z4.string()
});
var UpsertBannerRequestDto = z4.object({
  placement: BannerPlacementSchema,
  template: BannerTemplateSchema.default("compact"),
  preset: BannerPresetSchema.default("ocean_cyan"),
  eyebrow: z4.string().max(64).nullable().optional(),
  title: z4.string().min(1).max(140),
  subtitle: z4.string().max(280).nullable().optional(),
  badge: z4.string().max(48).nullable().optional(),
  ctaLabel: z4.string().max(48).nullable().optional(),
  ctaUrl: z4.string().max(500).nullable().optional(),
  imageDataUrl: z4.string().nullable().optional(),
  dismissible: z4.boolean().default(true),
  priority: z4.number().int().min(-100).max(1e3).default(0),
  active: z4.boolean().default(true),
  startsAt: z4.string().nullable().optional(),
  endsAt: z4.string().nullable().optional(),
  reason: z4.string().min(3).max(500).optional()
});

// src/cases.ts
import { z as z5 } from "zod";
var CaseLootDto = z5.object({
  type: z5.enum(["rub", "xp", "brainrot"]),
  value: z5.union([z5.number(), z5.string()]),
  weight: z5.number().int()
});
var CaseDto = z5.object({
  id: z5.string(),
  name: z5.string(),
  priceRub: z5.number(),
  priceGram: z5.number().optional(),
  variant: z5.enum(["common", "rare", "legend"]),
  loot: z5.array(CaseLootDto)
});
var CasesResponse = z5.object({ cases: z5.array(CaseDto) });
var OpenCaseResponse = z5.object({
  caseId: z5.string(),
  result: z5.object({
    type: z5.enum(["rub", "xp", "brainrot"]),
    value: z5.union([z5.number(), z5.string()])
  }),
  /** Index into the case's loot list — lets the client land its reel exactly. */
  lootIndex: z5.number().int(),
  /** New spendable balance (GRAM / legacy RUB) after debit + any reward. */
  balanceRub: z5.number(),
  balanceGram: z5.number().optional(),
  /** New XP total after any XP reward. */
  xp: z5.number().int(),
  /** Provable-fairness: hash of the server seed + nonce used for this open. */
  seedHash: z5.string(),
  nonce: z5.number().int()
});

// src/catalog.ts
import { z as z6 } from "zod";
var CatalogProviderSchema = z6.enum([
  "GAMESDROP",
  "PIKASIM",
  "PLAYWALLET",
  "MOOGOLD",
  "LOCAL_STOCK"
]);
var CatalogFulfillmentKindSchema = z6.enum([
  "VOUCHER",
  "STEAM_TOPUP",
  "GAME_UID",
  "ESIM"
]);
var CatalogInputModeSchema = z6.enum([
  "NONE",
  "STEAM_LOGIN",
  "UID_ONLY",
  "UID_AND_SERVER"
]);
var CatalogServerOptionDto = z6.object({
  id: z6.string().min(1),
  label: z6.string().min(1)
});
var OrderFulfillmentInputDto = z6.object({
  steamLogin: z6.string().trim().min(2).max(64).optional(),
  playerId: z6.string().trim().min(2).max(64).optional(),
  serverId: z6.string().trim().min(1).max(64).optional(),
  playerNickname: z6.string().trim().max(120).optional(),
  customAmountRub: z6.number().int().min(10).max(1e5).optional()
});
var OrderDeliveryRevealDto = z6.object({
  kind: z6.enum(["voucher", "topup", "esim"]),
  secretPlaintext: z6.string().nullable().optional(),
  iccid: z6.string().nullable().optional(),
  smdpAddress: z6.string().nullable().optional(),
  appleInstallUrl: z6.string().nullable().optional(),
  dataMb: z6.number().int().nullable().optional(),
  validityDays: z6.number().int().nullable().optional(),
  countryIso: z6.string().nullable().optional(),
  instructionsRu: z6.string().nullable().optional(),
  instructionsEn: z6.string().nullable().optional()
});
var PublicCatalogOfferDto = z6.object({
  id: z6.string(),
  titleRu: z6.string(),
  titleEn: z6.string(),
  priceRub: z6.number(),
  basePriceRub: z6.number(),
  priceGram: z6.number(),
  basePriceGram: z6.number().optional(),
  effectiveMarkupBp: z6.number().int().optional(),
  providerRateBp: z6.number().int().optional(),
  minAmountRub: z6.number().int().nullable(),
  maxAmountRub: z6.number().int().nullable(),
  dataMb: z6.number().int().nullable(),
  validityDays: z6.number().int().nullable(),
  inStock: z6.boolean()
});
var PublicCatalogVariantDto = z6.object({
  productId: z6.string(),
  slug: z6.string(),
  provider: CatalogProviderSchema,
  fulfillmentKind: CatalogFulfillmentKindSchema,
  inputMode: CatalogInputModeSchema,
  countryIso: z6.string().nullable(),
  regionLabelRu: z6.string(),
  regionLabelEn: z6.string(),
  instructionsRu: z6.string().nullable(),
  instructionsEn: z6.string().nullable(),
  serverOptions: z6.array(CatalogServerOptionDto),
  offers: z6.array(PublicCatalogOfferDto)
});
var PublicCatalogProductCardDto = z6.object({
  id: z6.string(),
  slug: z6.string(),
  brandKey: z6.string().nullable().optional(),
  categorySlug: z6.string(),
  provider: CatalogProviderSchema,
  fulfillmentKind: CatalogFulfillmentKindSchema,
  inputMode: CatalogInputModeSchema,
  titleRu: z6.string(),
  titleEn: z6.string(),
  descriptionRu: z6.string().nullable(),
  descriptionEn: z6.string().nullable(),
  imageUrl: z6.string().nullable(),
  countryIso: z6.string().nullable(),
  isHot: z6.boolean().optional(),
  accentColor: z6.string().nullable().optional(),
  availableRegions: z6.array(z6.string()).optional(),
  hasDirectTopup: z6.boolean().optional(),
  minPriceRub: z6.number(),
  minPriceGram: z6.number(),
  offersCount: z6.number().int()
});
var PublicCatalogCategoryDto = z6.object({
  id: z6.string(),
  slug: z6.string(),
  titleRu: z6.string(),
  titleEn: z6.string(),
  subtitleRu: z6.string().nullable(),
  subtitleEn: z6.string().nullable(),
  iconKey: z6.string(),
  products: z6.array(PublicCatalogProductCardDto)
});
var PublicCatalogResponse = z6.object({
  categories: z6.array(PublicCatalogCategoryDto)
});
var PublicCatalogProductDetailDto = z6.object({
  id: z6.string(),
  slug: z6.string(),
  brandKey: z6.string().nullable().optional(),
  categorySlug: z6.string(),
  provider: CatalogProviderSchema,
  fulfillmentKind: CatalogFulfillmentKindSchema,
  inputMode: CatalogInputModeSchema,
  titleRu: z6.string(),
  titleEn: z6.string(),
  descriptionRu: z6.string().nullable(),
  descriptionEn: z6.string().nullable(),
  instructionsRu: z6.string().nullable(),
  instructionsEn: z6.string().nullable(),
  imageUrl: z6.string().nullable(),
  countryIso: z6.string().nullable(),
  isHot: z6.boolean().optional(),
  accentColor: z6.string().nullable().optional(),
  gramRub: z6.number().optional(),
  serverOptions: z6.array(CatalogServerOptionDto),
  offers: z6.array(PublicCatalogOfferDto),
  variants: z6.array(PublicCatalogVariantDto).optional()
});
var ValidateCatalogRecipientRequest = z6.object({
  offerId: z6.string().uuid(),
  playerId: z6.string().trim().min(2).max(64),
  serverId: z6.string().trim().min(1).max(64).optional()
});
var ValidateCatalogRecipientResponse = z6.object({
  valid: z6.boolean(),
  nickname: z6.string().nullable().optional(),
  message: z6.string().nullable().optional()
});
var MyEsimItemDto = z6.object({
  orderId: z6.string(),
  productTitle: z6.string(),
  countryIso: z6.string().nullable(),
  iccid: z6.string().nullable(),
  lpaString: z6.string().nullable(),
  smdpAddress: z6.string().nullable(),
  activationCode: z6.string().nullable(),
  appleInstallUrl: z6.string().nullable(),
  totalMb: z6.number().int().nullable(),
  remainingMb: z6.number().int().nullable(),
  validityDays: z6.number().int().nullable(),
  providerStatus: z6.string().nullable(),
  expiresAt: z6.string().nullable(),
  deliveredAt: z6.string().nullable()
});
var MyEsimsResponse = z6.object({
  items: z6.array(MyEsimItemDto)
});
var AdminCatalogCategoryDto = z6.object({
  id: z6.string(),
  slug: z6.string(),
  titleRu: z6.string(),
  titleEn: z6.string(),
  subtitleRu: z6.string().nullable(),
  subtitleEn: z6.string().nullable(),
  iconKey: z6.string(),
  defaultMarkupBp: z6.number().int(),
  sortOrder: z6.number().int(),
  isPublished: z6.boolean()
});
var AdminCatalogOverviewDto = z6.object({
  gamesDropBalanceUsd: z6.number().nullable(),
  pikaSimBalanceUsd: z6.number().nullable(),
  playWalletBalanceUsd: z6.number().nullable().optional(),
  mooGoldBalanceUsd: z6.number().nullable().optional(),
  lastSyncedAt: z6.string().nullable(),
  counts: z6.object({
    totalProducts: z6.number().int(),
    publishedProducts: z6.number().int(),
    totalOffers: z6.number().int(),
    publishedOffers: z6.number().int(),
    outOfStockOffers: z6.number().int()
  }),
  categories: z6.array(AdminCatalogCategoryDto)
});
var AdminCatalogOfferDto = z6.object({
  id: z6.string(),
  productId: z6.string(),
  provider: CatalogProviderSchema,
  externalSku: z6.string(),
  titleRu: z6.string(),
  titleEn: z6.string(),
  wholesaleUsdMicros: z6.number().int(),
  wholesaleUsd: z6.number(),
  wholesaleRub: z6.number(),
  effectiveMarkupBp: z6.number().int(),
  overrideMarkupBp: z6.number().int().nullable(),
  providerRateBp: z6.number().int().positive().nullable().optional(),
  retailRub: z6.number(),
  retailGram: z6.number(),
  marginRub: z6.number(),
  minAmountRub: z6.number().int().nullable(),
  maxAmountRub: z6.number().int().nullable(),
  dataMb: z6.number().int().nullable(),
  validityDays: z6.number().int().nullable(),
  inStock: z6.boolean(),
  availableStockCount: z6.number().int().nonnegative().nullable().optional(),
  isPublished: z6.boolean(),
  sortOrder: z6.number().int(),
  lastSyncedAt: z6.string().nullable()
});
var AdminCatalogProductDto = z6.object({
  id: z6.string(),
  categoryId: z6.string(),
  categorySlug: z6.string(),
  slug: z6.string(),
  brandKey: z6.string().nullable().optional(),
  brandSubtitleRu: z6.string().nullable().optional(),
  brandSubtitleEn: z6.string().nullable().optional(),
  regionLabelRu: z6.string().nullable().optional(),
  regionLabelEn: z6.string().nullable().optional(),
  isHot: z6.boolean().optional(),
  accentColor: z6.string().nullable().optional(),
  provider: CatalogProviderSchema,
  fulfillmentKind: CatalogFulfillmentKindSchema,
  inputMode: CatalogInputModeSchema,
  titleRu: z6.string(),
  titleEn: z6.string(),
  descriptionRu: z6.string().nullable(),
  descriptionEn: z6.string().nullable(),
  instructionsRu: z6.string().nullable(),
  instructionsEn: z6.string().nullable(),
  imageUrl: z6.string().nullable(),
  countryIso: z6.string().nullable(),
  markupBp: z6.number().int().nullable(),
  sortOrder: z6.number().int(),
  isPublished: z6.boolean(),
  offers: z6.array(AdminCatalogOfferDto)
});
var AdminCatalogProductsResponse = z6.object({
  usdRub: z6.number(),
  gramRub: z6.number(),
  products: z6.array(AdminCatalogProductDto)
});
var AdminTriggerSyncRequest = z6.object({
  provider: z6.enum(["GAMESDROP", "PIKASIM", "PLAYWALLET", "MOOGOLD", "ALL"]).default("ALL")
});
var AdminTriggerSyncResponse = z6.object({
  syncedProducts: z6.number().int(),
  syncedOffers: z6.number().int(),
  syncedAt: z6.string()
});
var AdminUpdateCategoryRequest = z6.object({
  titleRu: z6.string().min(1).max(80).optional(),
  titleEn: z6.string().min(1).max(80).optional(),
  subtitleRu: z6.string().max(160).nullable().optional(),
  subtitleEn: z6.string().max(160).nullable().optional(),
  defaultMarkupBp: z6.number().int().min(0).max(5e4).optional(),
  sortOrder: z6.number().int().min(0).max(1e4).optional(),
  isPublished: z6.boolean().optional()
});
var AdminUpdateProductRequest = z6.object({
  titleRu: z6.string().min(1).max(120).optional(),
  titleEn: z6.string().min(1).max(120).optional(),
  brandKey: z6.string().trim().min(1).max(64).nullable().optional(),
  brandSubtitleRu: z6.string().max(160).nullable().optional(),
  brandSubtitleEn: z6.string().max(160).nullable().optional(),
  regionLabelRu: z6.string().max(64).nullable().optional(),
  regionLabelEn: z6.string().max(64).nullable().optional(),
  isHot: z6.boolean().optional(),
  accentColor: z6.string().max(24).nullable().optional(),
  descriptionRu: z6.string().max(500).nullable().optional(),
  descriptionEn: z6.string().max(500).nullable().optional(),
  instructionsRu: z6.string().max(2e3).nullable().optional(),
  instructionsEn: z6.string().max(2e3).nullable().optional(),
  imageUrl: z6.string().max(500).nullable().optional(),
  countryIso: z6.string().max(8).nullable().optional(),
  markupBp: z6.number().int().min(0).max(5e4).nullable().optional(),
  sortOrder: z6.number().int().min(0).max(1e4).optional(),
  isPublished: z6.boolean().optional()
});
var AdminUpdateOfferRequest = z6.object({
  titleRu: z6.string().min(1).max(140).optional(),
  titleEn: z6.string().min(1).max(140).optional(),
  wholesaleUsd: z6.number().positive().max(1e4).optional(),
  overrideMarkupBp: z6.number().int().min(0).max(5e4).nullable().optional(),
  sortOrder: z6.number().int().min(0).max(1e4).optional(),
  isPublished: z6.boolean().optional()
});
var AdminCreateLocalOfferRequest = z6.object({
  categorySlug: z6.enum(["services", "steam", "games"]).default("services"),
  brandKey: z6.string().trim().min(1).max(64),
  productSlug: z6.string().trim().min(2).max(80).optional(),
  productTitleRu: z6.string().trim().min(1).max(120),
  productTitleEn: z6.string().trim().min(1).max(120),
  countryIso: z6.string().trim().min(2).max(8).default("RU"),
  regionLabelRu: z6.string().trim().min(1).max(64).optional(),
  regionLabelEn: z6.string().trim().min(1).max(64).optional(),
  offerTitleRu: z6.string().trim().min(1).max(140),
  offerTitleEn: z6.string().trim().min(1).max(140),
  wholesaleUsd: z6.number().positive().max(1e4),
  instructionsRu: z6.string().max(2e3).nullable().optional(),
  instructionsEn: z6.string().max(2e3).nullable().optional(),
  isHot: z6.boolean().optional()
});
var AdminUploadVouchersRequest = z6.object({
  offerId: z6.string().min(1),
  codes: z6.array(z6.string().trim().min(2).max(512)).min(1).max(2e3),
  wholesaleUsd: z6.number().positive().max(1e4).optional()
});
var AdminUploadVouchersResponse = z6.object({
  offerId: z6.string(),
  insertedCount: z6.number().int().nonnegative(),
  duplicateCount: z6.number().int().nonnegative(),
  availableCount: z6.number().int().nonnegative()
});
var AdminBulkPublishModeSchema = z6.enum([
  "best_offers",
  "all_offers",
  "unpublish_all"
]);
var AdminBulkPublishCatalogRequest = z6.object({
  productIds: z6.array(z6.string().min(1)).min(1).max(2e4),
  mode: AdminBulkPublishModeSchema
});
var AdminBulkPublishCatalogResponse = z6.object({
  updatedProducts: z6.number().int(),
  publishedOffers: z6.number().int(),
  unpublishedOffers: z6.number().int()
});

// src/config.ts
import { z as z7 } from "zod";
var PublicConfigResponse = z7.object({
  botUsername: z7.string().nullable(),
  starPriceRub: z7.number(),
  /** Retail price per 1 Star in GRAM (Fragment base × STARS_MARKUP_MULTIPLIER). */
  starPriceGram: z7.number().default(0.0132),
  /** Pure Fragment conversion rate: how many GRAM 1 Telegram Star yields for game top-ups. */
  gramRatePerStar: z7.number().default(0.012),
  /** Active retail markup multipliers X configured in Admin. */
  starsMarkupMultiplier: z7.number().default(1.1),
  premiumMarkupMultiplier: z7.number().default(1.1),
  /** Daily CoinGecko FX rates (cached 24h) for currency conversion. */
  fx: z7.object({
    gramUsd: z7.number(),
    gramRub: z7.number(),
    usdRub: z7.number()
  }).optional(),
  /** Destination TON wallet address for TonConnect GRAM deposits. */
  tonDepositAddress: z7.string().nullable().default(null),
  packs: z7.array(z7.number().int()),
  /** Telegram Premium tiers: our retail price (RUB/GRAM/Stars) + official price. */
  premiumTiers: z7.array(
    z7.object({
      months: z7.number().int(),
      priceRub: z7.number(),
      priceGram: z7.number().optional(),
      priceStars: z7.number().int().optional(),
      officialRub: z7.number()
    })
  ),
  feeLadder: z7.array(
    z7.object({ lvl: z7.number().int(), title: z7.string(), feePercent: z7.number() })
  ),
  /** theme tokens (owner's chosen tweaks) */
  theme: z7.object({
    accent: z7.tuple([z7.string(), z7.string()]),
    background: z7.enum(["neon", "minimal", "aurora"]),
    glow: z7.number(),
    radius: z7.number()
  })
});

// src/games.ts
import { z as z8 } from "zod";
var GameRoundStatus = z8.enum(["WAITING", "RUNNING", "CRASHED"]);
var GameBetStatus = z8.enum(["PENDING", "CASHED_OUT", "LOST"]);
var GameRoundDto = z8.object({
  id: z8.string(),
  gameType: z8.string(),
  status: GameRoundStatus,
  /** Crash point in basis points, null until round crashes. */
  crashPoint: z8.number().int().nullable(),
  serverSeedHash: z8.string(),
  nonce: z8.number().int(),
  startedAt: z8.string().nullable(),
  crashedAt: z8.string().nullable(),
  createdAt: z8.string()
});
var GameBetDto = z8.object({
  id: z8.string(),
  roundId: z8.string(),
  amountKopecks: z8.number().int(),
  cashoutMultiplier: z8.number().int().nullable(),
  payoutKopecks: z8.number().int().nullable(),
  status: GameBetStatus,
  createdAt: z8.string()
});
var CrashRoundPlayerDto = z8.object({
  betId: z8.string(),
  userId: z8.string(),
  name: z8.string(),
  username: z8.string().nullable().optional(),
  isUsernameHidden: z8.boolean().default(false),
  photoUrl: z8.string().nullable(),
  isVerified: z8.boolean().default(false),
  isMe: z8.boolean(),
  isHidden: z8.boolean().default(false),
  amountKopecks: z8.number().int(),
  cashoutMultiplier: z8.number().int().nullable(),
  payoutKopecks: z8.number().int().nullable(),
  status: GameBetStatus
});
var GameCurrentResponse = z8.object({
  round: GameRoundDto.nullable(),
  bets: z8.array(GameBetDto),
  roundPlayers: z8.array(CrashRoundPlayerDto).default([]),
  /** Current multiplier value (1.xx) — -1 when not running, null when no round. */
  multiplier: z8.number().nullable(),
  /** Server wall-clock (ms) at response time — lets the client sync its clock so
   * the multiplier it renders exactly matches the server's. */
  serverNowMs: z8.number()
});
var GameHistoryResponse = z8.object({
  rounds: z8.array(GameRoundDto)
});
var PlaceBetRequest = z8.object({
  amountKopecks: z8.number().int().min(100).max(1e7)
});
var PlaceBetResponse = z8.object({
  bet: GameBetDto,
  balanceAfter: z8.number().int()
});
var CashoutResponse = z8.object({
  bet: GameBetDto,
  payoutKopecks: z8.number().int(),
  multiplier: z8.number(),
  balanceAfter: z8.number().int()
});
var SseEvent = z8.object({
  type: z8.enum(["round_status", "multiplier", "crash", "cashout", "bet_placed"]),
  data: z8.record(z8.string(), z8.any())
});
var DepositRequest = z8.object({
  amountRub: z8.number().positive()
});

// src/orders.ts
import { z as z9 } from "zod";
var RecipientType = z9.enum(["self", "friend"]);
var ProductType = z9.enum(["stars", "premium", "deposit", "catalog"]);
var OrderPaymentMethod = z9.enum(["rub", "gram", "stars"]);
var PremiumMonths = z9.union([z9.literal(3), z9.literal(6), z9.literal(12)]);
var OrderStatus = z9.enum([
  "CREATED",
  "AWAITING_PAYMENT",
  "AWAITING_INITIALS",
  "MATCHING",
  "MATCHED",
  "DELIVERING",
  "DELIVERING_ASYNC",
  "DELIVERED",
  "DELIVERY_FAILED",
  "REFUNDED",
  "EXPIRED",
  "CANCELLED"
]);
var QuoteRequest = z9.object({
  product: z9.enum(["stars", "premium"]).default("stars"),
  qty: z9.number().int().min(1).max(1e5).optional(),
  months: PremiumMonths.optional()
});
var PriceBreakdownDto = z9.object({
  qty: z9.number(),
  starPriceRub: z9.number(),
  feePercent: z9.number(),
  total: z9.number(),
  fee: z9.number(),
  grand: z9.number()
});
var QuoteResponse = PriceBreakdownDto;
var CreateOrderRequest = z9.object({
  product: ProductType.default("stars"),
  /** required for product = 'stars' */
  qty: z9.number().int().min(1).max(1e5).optional(),
  /** required for product = 'premium' */
  months: PremiumMonths.optional(),
  /** required for product = 'catalog' */
  offerId: z9.string().uuid().optional(),
  /** optional recipient/account details for product = 'catalog' */
  fulfillmentInput: OrderFulfillmentInputDto.optional(),
  recipientType: RecipientType,
  /** required when recipientType = 'friend' */
  recipientUsername: z9.string().optional(),
  /** chosen checkout payment method ('rub' | 'gram' | 'stars') */
  paymentMethod: OrderPaymentMethod.optional()
}).refine(
  (d) => d.product === "premium" ? d.months != null : d.product === "catalog" ? Boolean(d.offerId) : d.qty != null,
  {
    message: "qty is required for stars, months for premium, offerId for catalog"
  }
).refine((d) => !(d.product === "catalog" && d.paymentMethod === "stars"), {
  message: "\u041E\u043F\u043B\u0430\u0442\u0430 Telegram Stars \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430 \u0434\u043B\u044F \u0432\u043D\u0435\u0448\u043D\u0438\u0445 \u0442\u043E\u0432\u0430\u0440\u043E\u0432 \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0430"
});
var PaymentInstructions = z9.object({
  card: z9.string().nullable(),
  phone: z9.string().nullable(),
  bankName: z9.string(),
  recipientName: z9.string().nullable(),
  /** optional static transfer link to render as a scannable QR */
  qrLink: z9.string().nullable(),
  /** exact amount to transfer, RUB with kopecks (e.g. 1247.83) */
  amountRub: z9.number(),
  amountKopecks: z9.number().int()
});
var OrderResponse = z9.object({
  id: z9.string(),
  status: OrderStatus,
  product: ProductType,
  qty: z9.number().int(),
  /** Premium duration in months for product = 'premium', else null. */
  months: z9.number().int().nullable(),
  recipientType: RecipientType,
  recipientUsername: z9.string().nullable(),
  /** referral balance applied to this order (RUB); bank pays only the remainder */
  balanceAppliedRub: z9.number(),
  breakdown: PriceBreakdownDto,
  payment: PaymentInstructions,
  initialsSubmitted: z9.boolean(),
  paymentMethod: OrderPaymentMethod.optional(),
  starsInvoiceUrl: z9.string().nullable().optional(),
  gramCharged: z9.number().optional(),
  offerId: z9.string().nullable().optional(),
  productTitle: z9.string().nullable().optional(),
  externalProvider: z9.string().optional(),
  deliveryReveal: OrderDeliveryRevealDto.nullable().optional(),
  createdAt: z9.string(),
  expiresAt: z9.string()
});
var CreateDepositRequest = z9.object({
  amountRub: z9.number().min(10).max(1e5)
});
var DepositOrderResponse = z9.object({
  id: z9.string(),
  status: OrderStatus,
  amountKopecks: z9.number().int(),
  amountRub: z9.number(),
  payment: PaymentInstructions,
  createdAt: z9.string(),
  expiresAt: z9.string()
});
var SubmitInitialsRequest = z9.object({
  initials: z9.string().min(2).max(80)
});
var CreateStarsDepositRequest = z9.object({
  stars: z9.number().int().min(10).max(1e5)
});
var StarsDepositInvoiceResponse = z9.object({
  depositId: z9.string(),
  invoiceUrl: z9.string(),
  stars: z9.number().int(),
  gramAmount: z9.number(),
  rateGramPerStar: z9.number()
});
var CreateTonDepositRequest = z9.object({
  gramAmount: z9.number().min(0.1).max(1e5)
});
var TonDepositResponse = z9.object({
  depositId: z9.string(),
  toAddress: z9.string(),
  amountNano: z9.string(),
  memo: z9.string(),
  commentBoc: z9.string().optional(),
  gramAmount: z9.number()
});
var TonDepositStatusResponse = z9.object({
  depositId: z9.string(),
  status: z9.enum(["PENDING", "COMPLETED", "EXPIRED"]),
  gramAmount: z9.number(),
  balanceGram: z9.number()
});

// src/profile.ts
import { z as z10 } from "zod";
var LevelInfoDto = z10.object({
  lvl: z10.number().int(),
  title: z10.string(),
  feePercent: z10.number(),
  into: z10.number(),
  span: z10.number(),
  progress: z10.number(),
  isMax: z10.boolean(),
  next: z10.object({ lvl: z10.number().int(), title: z10.string(), reqXp: z10.number().int() }).nullable()
});
var BrainrotDto = z10.object({
  id: z10.string(),
  name: z10.string(),
  unlockLvl: z10.number().int(),
  rarity: z10.string(),
  /** rarity accent colour */
  rc: z10.string(),
  /** unlocked by the user's level */
  unlocked: z10.boolean(),
  /** owned (level-unlocked OR won in a case / picked at onboarding) */
  owned: z10.boolean(),
  /** currently equipped as the active companion */
  active: z10.boolean()
});
var FishStateDto = z10.object({
  hunger: z10.number().int().min(0).max(100),
  happiness: z10.number().int().min(0).max(100),
  health: z10.number().int().min(0).max(100),
  status: z10.enum(["thriving", "ok", "hungry", "critical", "dead"]),
  isDead: z10.boolean(),
  xpMultiplier: z10.number(),
  canFeed: z10.boolean(),
  nextFeedAt: z10.string().nullable(),
  canPlay: z10.boolean(),
  nextPlayAt: z10.string().nullable(),
  reviveCostRub: z10.number(),
  reviveCostGram: z10.number().optional()
});
var FishReviveRequest = z10.object({
  mode: z10.enum(["balance", "reset"])
});
var UserBanAppealStateSchema = z10.enum(["OPEN", "INFO_REQUESTED", "APPROVED", "REJECTED"]);
var UserBanStatusDto = z10.object({
  bannedAt: z10.string(),
  bannedUntil: z10.string().nullable(),
  reason: z10.string(),
  activeAppealId: z10.string().nullable(),
  appealState: UserBanAppealStateSchema.nullable().optional()
});
var UserCommissionDto = z10.object({
  source: z10.enum(["general", "spend_tier", "personal"]),
  starsSource: z10.enum(["general", "spend_tier", "personal"]),
  premiumSource: z10.enum(["general", "spend_tier", "personal"]),
  generalStarsFeePercent: z10.number().optional(),
  generalPremiumFeePercent: z10.number().optional(),
  starsFeePercent: z10.number(),
  premiumFeePercent: z10.number(),
  starsDiscountPercent: z10.number().optional(),
  premiumDiscountPercent: z10.number().optional(),
  starsMarkup: z10.number(),
  premiumMarkup: z10.number(),
  spentRub: z10.number(),
  currentTierMinRub: z10.number().nullable(),
  nextTier: z10.object({
    minSpentRub: z10.number(),
    remainingRub: z10.number(),
    starsFeePercent: z10.number(),
    premiumFeePercent: z10.number()
  }).nullable(),
  spendTiers: z10.array(
    z10.object({
      minSpentRub: z10.number(),
      starsFeePercent: z10.number(),
      premiumFeePercent: z10.number(),
      label: z10.string().nullable().optional()
    })
  ).default([]),
  starPriceRub: z10.number(),
  starPriceGram: z10.number(),
  premiumTiers: z10.array(
    z10.object({
      months: z10.union([z10.literal(3), z10.literal(6), z10.literal(12)]),
      priceRub: z10.number(),
      priceGram: z10.number(),
      priceStars: z10.number().int(),
      officialRub: z10.number()
    })
  )
});
var ProfileResponse = z10.object({
  userId: z10.string().optional(),
  telegramId: z10.number(),
  username: z10.string().nullable(),
  firstName: z10.string().nullable(),
  photoUrl: z10.string().nullable(),
  customName: z10.string().nullable().optional(),
  customPhotoUrl: z10.string().nullable().optional(),
  hideUsername: z10.boolean().default(false),
  language: z10.enum(["ru", "en"]).default("ru"),
  canBindReferral: z10.boolean().default(false),
  appliedReferralCode: z10.string().nullable().optional(),
  isVerified: z10.boolean().default(false),
  createdAt: z10.string(),
  xp: z10.number().int(),
  purchasedStars: z10.number().int(),
  premiumCount: z10.number().int(),
  friendsCount: z10.number().int(),
  /** spendable referral balance in RUB */
  balanceRub: z10.number(),
  /** spendable game balance in GRAM */
  balanceGram: z10.number().default(0),
  level: LevelInfoDto,
  /** fee % actually applied to the next order (incl. referee first-purchase discount) */
  feePercentEffective: z10.number(),
  /** one-time first-order referral discount % (0 when ineligible or already consumed) */
  firstOrderDiscountPercent: z10.number().default(0),
  /** one-time first-deposit bonus % (0 when ineligible or already consumed) */
  firstDepositBonusPercent: z10.number().default(0),
  /** personalized commission rates, spend-tier progress, and resolved retail prices */
  commission: UserCommissionDto.optional(),
  collection: z10.array(BrainrotDto),
  /** id of the equipped companion (drives the HUD / nav / profile mascot) */
  activeBrainrot: z10.string(),
  /** Tamagotchi live fish care state */
  fish: FishStateDto,
  /** consecutive-day login streak */
  streak: z10.number().int(),
  /** whether the daily reward has already been claimed today */
  dailyClaimedToday: z10.boolean(),
  /** whether the user has completed onboarding (picked a starter companion) */
  onboarded: z10.boolean(),
  /** optional active ban status for BannedScreen in Mini App */
  ban: UserBanStatusDto.nullable().optional()
});
var CompanionRequest = z10.object({ brainrotId: z10.string() });
var OnboardRequest = z10.object({
  brainrotId: z10.string(),
  referralCode: z10.string().trim().max(128).optional()
});
var UpdateProfileSettingsRequest = z10.object({
  customName: z10.string().trim().min(1).max(32).nullable().optional(),
  customPhotoUrl: z10.string().max(4e5).nullable().optional(),
  hideUsername: z10.boolean().optional(),
  language: z10.enum(["ru", "en"]).optional()
});
var SubmitBanAppealRequest = z10.object({
  message: z10.string().trim().min(5).max(2e3),
  attachmentIds: z10.array(z10.string().min(1)).max(5).optional()
});
var SubmitBanAppealResponse = z10.object({
  id: z10.string(),
  appealNumber: z10.number().int(),
  state: UserBanAppealStateSchema,
  alreadyOpen: z10.boolean().default(false)
});
var CreateMiniAppTicketRequest = z10.object({
  category: SupportTicketCategorySchema.default("other"),
  message: z10.string().trim().min(3).max(2e3),
  attachmentIds: z10.array(z10.string().min(1)).max(5).optional()
});
var CreateMiniAppTicketResponse = z10.object({
  id: z10.string(),
  ticketNumber: z10.number().int(),
  state: z10.string(),
  appendedToOpen: z10.boolean().default(false)
});
var ProfileTelemetryRequest = z10.object({
  telegramPlatform: z10.string().trim().max(48).optional(),
  telegramVersion: z10.string().trim().max(32).optional(),
  languageCode: z10.string().trim().max(16).optional(),
  isPremium: z10.boolean().optional(),
  screenResolution: z10.string().trim().max(32).optional(),
  timezone: z10.string().trim().max(64).optional(),
  deviceFingerprint: z10.string().trim().max(128).optional()
});

// src/social.ts
import { z as z11 } from "zod";
var LeaderboardEntry = z11.object({
  userId: z11.string().optional(),
  rank: z11.number().int(),
  name: z11.string(),
  username: z11.string().nullable().optional(),
  isUsernameHidden: z11.boolean().default(false),
  photoUrl: z11.string().nullable().optional(),
  isVerified: z11.boolean().default(false),
  xp: z11.number().int(),
  isMe: z11.boolean(),
  isHidden: z11.boolean().optional()
});
var LeaderboardResponse = z11.object({
  entries: z11.array(LeaderboardEntry),
  myRank: z11.number().int().nullable()
});
var ReferralResponse = z11.object({
  code: z11.string(),
  link: z11.string(),
  invited: z11.number().int(),
  xpEarned: z11.number().int(),
  /** ₽ cashback earned from referrals' purchases (credited to balance) */
  earnedRub: z11.number(),
  hasReferrer: z11.boolean().default(false),
  canBindCode: z11.boolean().default(false),
  appliedCode: z11.string().nullable().optional(),
  firstOrderDiscountPercent: z11.number().default(0),
  firstDepositBonusPercent: z11.number().default(0)
});
var BindReferralRequest = z11.object({
  code: z11.string().trim().min(2).max(128)
});
var BindReferralResponse = z11.object({
  applied: z11.boolean(),
  appliedCode: z11.string().nullable().optional(),
  referrerName: z11.string().nullable().optional(),
  discountPercent: z11.number().default(0),
  firstDepositBonusPercent: z11.number().default(0)
});
var DailyClaimResponse = z11.object({
  streak: z11.number().int(),
  xpAwarded: z11.number().int(),
  alreadyClaimed: z11.boolean(),
  /** new XP total after the claim */
  xp: z11.number().int()
});
var QuestDto = z11.object({
  id: z11.string(),
  key: z11.string().optional(),
  label: z11.string(),
  subtitle: z11.string().nullable().optional(),
  progress: z11.number().int(),
  goal: z11.number().int(),
  rewardXp: z11.number().int(),
  rewardRub: z11.number().default(0),
  done: z11.boolean(),
  claimed: z11.boolean()
});
var QuestsResponse = z11.object({ quests: z11.array(QuestDto) });
var ClaimQuestRequest = z11.object({ questId: z11.string() });
var UserReportReasonSchema = z11.enum([
  "inappropriate_name",
  "inappropriate_avatar",
  "abuse_spam",
  "scam"
]);
var UserReportContextSchema = z11.enum(["leaderboard", "crash", "profile"]);
var CreateUserReportRequest = z11.object({
  targetUserId: z11.string().min(1),
  context: UserReportContextSchema.default("leaderboard"),
  reason: UserReportReasonSchema,
  comment: z11.string().trim().max(500).optional(),
  attachmentIds: z11.array(z11.string().min(1)).max(5).optional()
});
var CreateUserReportResponse = z11.object({
  id: z11.string(),
  reportNumber: z11.number().int(),
  alreadyReported: z11.boolean().default(false)
});
export {
  AdminActionBatchDto,
  AdminActionBatchItemDto,
  AdminActionBatchItemKindSchema,
  AdminAuditLogDto,
  AdminAuthSessionDto,
  AdminBanAppealDto,
  AdminBanAppealEventDto,
  AdminBannerDto,
  AdminBotStarsSummaryDto,
  AdminBroadcastCampaignDto,
  AdminBulkPublishCatalogRequest,
  AdminBulkPublishCatalogResponse,
  AdminBulkPublishModeSchema,
  AdminCatalogCategoryDto,
  AdminCatalogOfferDto,
  AdminCatalogOverviewDto,
  AdminCatalogProductDto,
  AdminCatalogProductsResponse,
  AdminCategoryBreakdownItemDto,
  AdminCategoryBreakdownResponseDto,
  AdminCreateLocalOfferRequest,
  AdminDailyStatRowDto,
  AdminDashboardDto,
  AdminExecuteActionRequest,
  AdminFragmentSessionDetailDto,
  AdminFxDto,
  AdminGamesOverviewDto,
  AdminKpiMetricDto,
  AdminLinkedAccountDto,
  AdminLinkedAccountReasonDto,
  AdminOperatorEntryDto,
  AdminPricingLiveComparisonDto,
  AdminPricingPreviewDto,
  AdminReferralCodeDto,
  AdminReferralOverviewDto,
  AdminReferralTreeDto,
  AdminReferralTreeNodeDto,
  AdminReplySupportTicketRequestDto,
  AdminResolveUserReportRequestDto,
  AdminSearchResponseDto,
  AdminSortDirectionSchema,
  AdminSpendCommissionTierDto,
  AdminStepUpGrantDto,
  AdminTransactionRowDto,
  AdminTreasuryDto,
  AdminTriggerSyncRequest,
  AdminTriggerSyncResponse,
  AdminUpdateCategoryRequest,
  AdminUpdateOfferRequest,
  AdminUpdateProductRequest,
  AdminUpdateSpendTiersSchema,
  AdminUpdateUserCommissionSchema,
  AdminUpdateUserRefCodePerksSchema,
  AdminUploadVouchersRequest,
  AdminUploadVouchersResponse,
  AdminUpsertReferralCodeSchema,
  AdminUserCommissionDto,
  AdminUserDetailDto,
  AdminUserDeviceDto,
  AdminUserFinancialSummaryDto,
  AdminUserLoginEventDto,
  AdminUserReferralStatsDto,
  AdminUserReportDto,
  AdminUserSessionDto,
  AdminUsersSortBySchema,
  BalanceDriftDto,
  BannerPlacementSchema,
  BannerPresetSchema,
  BannerTemplateSchema,
  BindReferralRequest,
  BindReferralResponse,
  BrainrotDto,
  CaseDto,
  CaseLootDto,
  CasesResponse,
  CashoutResponse,
  CatalogFulfillmentKindSchema,
  CatalogInputModeSchema,
  CatalogProviderSchema,
  CatalogServerOptionDto,
  ClaimQuestRequest,
  CompanionRequest,
  CrashRoundPlayerDto,
  CreateDepositRequest,
  CreateMiniAppTicketRequest,
  CreateMiniAppTicketResponse,
  CreateOrderRequest,
  CreateStarsDepositRequest,
  CreateTonDepositRequest,
  CreateUserReportRequest,
  CreateUserReportResponse,
  DailyClaimResponse,
  DepositOrderResponse,
  DepositRequest,
  FishReviveRequest,
  FishStateDto,
  GameBetDto,
  GameBetStatus,
  GameCurrentResponse,
  GameHistoryResponse,
  GameRoundDto,
  GameRoundStatus,
  LeaderboardEntry,
  LeaderboardResponse,
  LevelInfoDto,
  MediaAttachmentDto,
  MediaAttachmentScopeSchema,
  MyEsimItemDto,
  MyEsimsResponse,
  OnboardRequest,
  OpenCaseResponse,
  OperatorAlertCountDto,
  OperatorAlertEventDto,
  OperatorAlertKindSchema,
  OperatorAlertSeveritySchema,
  OrderDeliveryRevealDto,
  OrderFulfillmentInputDto,
  OrderPaymentMethod,
  OrderResponse,
  OrderStatus,
  PaymentInstructions,
  PlaceBetRequest,
  PlaceBetResponse,
  PlatformPermissionSchema,
  PlatformRoleSchema,
  PremiumMonths,
  PriceBreakdownDto,
  ProductType,
  ProfileResponse,
  ProfileTelemetryRequest,
  PublicBannerDto,
  PublicBannersResponseDto,
  PublicCatalogCategoryDto,
  PublicCatalogOfferDto,
  PublicCatalogProductCardDto,
  PublicCatalogProductDetailDto,
  PublicCatalogResponse,
  PublicCatalogVariantDto,
  PublicConfigResponse,
  QuestDto,
  QuestsResponse,
  QuoteRequest,
  QuoteResponse,
  RecipientType,
  ReconcileSnapshotDto,
  ReferralResponse,
  SseEvent,
  StarsDepositInvoiceResponse,
  StepUpActionSchema,
  StuckOrderHealthDto,
  SubmitBanAppealRequest,
  SubmitBanAppealResponse,
  SubmitInitialsRequest,
  SupportTicketCategorySchema,
  SupportTicketDetailDto,
  SupportTicketDto,
  SupportTicketEventDto,
  SupportTicketFileDto,
  SupportTicketStateSchema,
  TonDepositResponse,
  TonDepositStatusResponse,
  UpdateProfileSettingsRequest,
  UploadMediaResponse,
  UpsertBannerRequestDto,
  UserBanAppealStateSchema,
  UserBanStatusDto,
  UserCommissionDto,
  UserReportContextSchema,
  UserReportReasonSchema,
  ValidateCatalogRecipientRequest,
  ValidateCatalogRecipientResponse
};
